import { LIMITS } from './constants.js';
import type { SubscriptionPlan } from './types.js';

/**
 * AUTH-009/010 — what each plan is allowed to do.
 *
 * The table lives in shared on purpose: the API enforces it and the billing
 * screen displays it, and the two disagreeing is how a customer ends up seeing
 * "4 of 5 galleries" while the server refuses the fifth. One table, both readers.
 *
 * `null` means unlimited. It is not a large number: a plan whose cap is
 * `999999` still prints a cap, and every comparison against it has to remember
 * that the number is a lie.
 */

/** Provider-independent lifecycle. See docs/01-features/11-billing-subscriptions.md. */
export const SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'cancelled', 'expired'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/**
 * The countable resources a plan caps. Each key is enforced server-side.
 *
 * Seats are not among them. An account is one person — the business that
 * signed up — so there was never a second seat to sell, and a cap of "5 users"
 * on a product that cannot create a second user is a number that only ever
 * misled the person reading the price list.
 */
export interface PlanLimits {
  connectedMailAccounts: number | null;
  galleries: number | null;
  quotesPerMonth: number | null;
  /**
   * Unified monthly actions per user: a successful agent request counts once,
   * as does a voice note that became a draft. Failed work and dictation are free.
   * The historical field name is retained for persisted back-office overrides.
   */
  voiceActionsPerMonth: number | null;
}

export interface PlanDefinition {
  id: SubscriptionPlan;
  /** Monthly price in agorot, before VAT — money never travels as a float. */
  monthlyPriceAgorot: number;
  limits: PlanLimits;
}

export const PLANS: Record<SubscriptionPlan, PlanDefinition> = {
  starter: {
    id: 'starter',
    monthlyPriceAgorot: 4900,
    limits: { connectedMailAccounts: 1, galleries: 5, quotesPerMonth: 20, voiceActionsPerMonth: 30 },
  },
  business: {
    id: 'business',
    monthlyPriceAgorot: 14900,
    limits: { connectedMailAccounts: 2, galleries: 20, quotesPerMonth: null, voiceActionsPerMonth: 200 },
  },
  pro: {
    id: 'pro',
    monthlyPriceAgorot: 29900,
    limits: { connectedMailAccounts: 2, galleries: null, quotesPerMonth: null, voiceActionsPerMonth: 1000 },
  },
};

/** Days of full access at signup, no card asked for. */
export const TRIAL_DAYS = 14;

/**
 * Days a failed charge keeps full access before the account degrades to
 * read-only. A business locked out of its own customer list the morning a card
 * expires will not renew — it will export and leave.
 */
export const PAST_DUE_GRACE_DAYS = 7;

/**
 * The stricter of what was bought and what the product supports. How many
 * galleries a mini-site can carry is a property of the mini-site, not of the
 * price list, so an "unlimited" plan still stops at the hard cap.
 *
 * Takes the caps rather than the plan id because the price list is editable
 * from the back office: the server resolves the stored overrides first and
 * passes the result through here, and the plan picker does the same with the
 * plans it was handed. Three readers, one ceiling — which is the only reason
 * the enforced number and the displayed one cannot drift apart.
 */
export function capLimits(limits: PlanLimits): PlanLimits {
  return {
    ...limits,
    galleries: limits.galleries === null ? LIMITS.maxGalleries : Math.min(limits.galleries, LIMITS.maxGalleries),
  };
}

/** The caps a plan gets as shipped, before any back-office override. */
export function planLimits(plan: SubscriptionPlan): PlanLimits {
  return capLimits((PLANS[plan] ?? PLANS.business).limits);
}

/** True when one more of this resource is allowed. `null` (unlimited) always is. */
export function withinLimit(limit: number | null, current: number): boolean {
  return limit === null || current < limit;
}

/**
 * Whether the subscription still grants write access. `trialing` and `active`
 * obviously do; `past_due` does until the grace period runs out, which is why
 * this needs the date and not just the status.
 */
export function grantsWriteAccess(
  status: SubscriptionStatus,
  since: { pastDueSince: string | null; currentPeriodEnd?: string | null; now?: Date },
): boolean {
  if (status === 'trialing' || status === 'active') return true;
  if (status === 'cancelled') {
    const paidUntil=since.currentPeriodEnd ? new Date(since.currentPeriodEnd).getTime() : NaN;
    return Number.isFinite(paidUntil) && (since.now ?? new Date()).getTime() < paidUntil;
  }
  if (status !== 'past_due') return false;
  if (!since.pastDueSince) return true;
  const deadline = new Date(since.pastDueSince).getTime() + PAST_DUE_GRACE_DAYS * 24 * 60 * 60 * 1000;
  return (since.now ?? new Date()).getTime() < deadline;
}
